import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, isRef as _isRef, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { value: "" }
const _hoisted_2 = ["value"]

import { ref, onMounted, watch, computed } from 'vue';
import { MstMyPageItem } from '@/types/mstMyPage';
import { RuleExpression, useField } from 'vee-validate';
import { useStore } from 'vuex';
import ValidatableField from '@/components/ValidatableField.vue';
import { MYPAGE_CONSTANTS } from '@/constants/mypage';

type SelectOption = {
  id: string | number;
  text: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ResumeSelect',
  props: {
    name: null,
    id: null,
    label: { default: '' },
    rules: null,
    modelValue: { default: '' },
    className: null,
    emptyText: { default: '選択してください' },
    isFilter: { type: Boolean, default: false }
  },
  emits: ['update:modelValue'],
  setup(__props: any, { emit }) {

const props = __props as { name: string, id: string, label: string, rules?: RuleExpression<unknown>, modelValue: string | number, className: string, emptyText: string, isFilter: boolean }





const options = ref<Array<SelectOption>>([]);
const store = useStore();

// Use useField to connect with vee-validate
const {
  value
} = useField(props.name, props.rules, {
  label: props.label,
  initialValue: props.modelValue
});

// Watch for changes in modelValue to update value
watch(() => props.modelValue, (newVal) => {
  if (newVal !== undefined && newVal !== value.value) {
    value.value = newVal;
  }
});

// Watch for changes in value to emit update event
watch(value, (newVal) => {
  emit('update:modelValue', newVal);
});

async function mapToSelectOption(data: MstMyPageItem[]) {
  return data?.map(p => ({
    id: p.id,
    text: p.text
  }));
}

async function loadSelectData() {
  const storeBase = MYPAGE_CONSTANTS.STORE_MST;
  try {
    switch (props.id) {
      case 'languageLevel': {
        const data = await store.dispatch(storeBase.MST_LANGUAGE_LEVEL);
        options.value = await mapToSelectOption(data);
        break;
      }
      case 'otherLang': {
        const data = await store.dispatch(storeBase.MST_OTRLANG);
        options.value = await mapToSelectOption(data);
        break;
      }
      case 'pref': {
        const data = await store.dispatch(storeBase.MST_PREF);
        options.value = await mapToSelectOption(data);
        break;
      }
      case 'walkBus': {
        const data = await store.dispatch(storeBase.MST_WALK_BUS);
        options.value = await mapToSelectOption(data);
        break;
      }
       case 'inHabitType': {
        const data = await store.dispatch(storeBase.MST_INHABIT_TYPE);
        options.value = await mapToSelectOption(data);
        break;
      }
      case 'allowance': {
        const data = await store.dispatch(storeBase.MST_ALLOWANCE);
        options.value = await mapToSelectOption(data);
        break;
      }
      case 'lstschl': {
        const data = await store.dispatch(storeBase.MST_LSTSCHL);
        options.value = await mapToSelectOption(data);
        break;
      }
      case 'grad': {
        const data = await store.dispatch(storeBase.MST_GRADUATION);
        options.value = await mapToSelectOption(data);
        break;
      }
      case 'entrance': {
        const data = await store.dispatch(storeBase.MST_ENTRANCE);
        options.value = await mapToSelectOption(data);
        break;
      }
      case 'emptype': {
        const data = await store.dispatch(storeBase.MST_EMPTYPE);
        options.value = await mapToSelectOption(data);
        break;
      }
      case 'listed': {
        const data = await store.dispatch(storeBase.MST_LISTED);
        options.value = await mapToSelectOption(data);
        break;
      }
      case 'yearCategory': {
        const data = await store.dispatch(storeBase.MST_YEAR_CATEGORY);
        options.value = await mapToSelectOption(data);
        break;
      }
      case 'managementYearCategory': {
        const data = await store.dispatch(storeBase.MST_PEOPLE_MANAGEMENT_YEAR);
        options.value = await mapToSelectOption(data);
        break;
      }
      case 'peopleManagement': {
        const data = await store.dispatch(storeBase.MST_PEOPLE_MANAGEMENT);
        options.value = await mapToSelectOption(data);
        break;
      }
      case 'spousePresence':
      case 'spouseObligation': {
        options.value = [
          {id: 1, text:'あり'},
          {id: 2, text:'なし'}
        ];
        break;
      }
      case 'sex': {
        options.value = [
          {id: 1, text:'男性'},
          {id: 2, text:'女性'}
        ];
        break;
      }

    }
  } catch (error) {
    console.error(`Error loading select data for ${props.name}:`, error);
  }
}

// MYNAVI_CP2-3512 【マイページ:書類作成】資格・語学、住所部分の修正
// 1. 除外対象となる旧マスタID（listedId）の定義
// 1:東証一部, 2:東証二部, 3:JASDAQ, 4:ヘラクレス, 5:マザーズ, 6:大証一部, 7:大証二部
const EXCLUDED_LISTED_IDS = new Set(['1', '2', '3', '4', '5', '6', '7']);

// 2. 「DB登録済みの元値（不変値）」の不変ロック処理
// 画面ロード時に非表示項目が登録されていた場合、ユーザーの操作で値が切り替わっても
// 保存（DB更新）が行われない限り値を「セッション中不動の元値」としてメモリに保持
const originalDbValue = ref<string | number | null>(null);

watch(
  () => value.value,
  (newVal) => {
    // 初回のみ有効な初期値ロック
    if (newVal !== undefined && newVal !== null && newVal !== '' && originalDbValue.value === null) {
      originalDbValue.value = newVal;
    }
  },
  { immediate: true }
);

// 3. キャッシュを活用した表示選択肢（options）のフィルタリング
const displayedOptions = computed(() => {
  // フィルターフラグが立っていない、または対象IDが'listed'以外の場合は全件返却
  if (!props.isFilter || props.id !== 'listed') {
    return options.value;
  }

  // ロックされている初期値の文字列標準化
  const originId = originalDbValue.value !== null ? String(originalDbValue.value).trim() : null;

  return options.value.filter(option => {
    // マスタデータのIDを文字列にクリーンアップ
    const targetId = String(option.id).trim();
    // ① 元値（非表示項目）は、ユーザーが他値を選択しても保存するまでは常に選択肢に復元・維持
    if (originId !== null && targetId === originId) {
      return true;
    }
    // ② 新規選択用の項目において、除外マスタ（1〜7）以外のみを通過
    return !EXCLUDED_LISTED_IDS.has(targetId);
  });
});

onMounted(async () => {
  await loadSelectData();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ValidatableField, {
    as: "select",
    class: _normalizeClass([__props.className, {'--has-value': !!_unref(value)}]),
    id: __props.id || __props.name,
    name: __props.name,
    rules: __props.rules,
    label: __props.label,
    modelValue: _unref(value),
    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(value) ? (value).value = $event : null))
  }, {
    default: _withCtx(() => [
      _createElementVNode("option", _hoisted_1, _toDisplayString(__props.emptyText), 1),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(displayedOptions), (option) => {
        return (_openBlock(), _createElementBlock("option", {
          key: option.id,
          value: option.id
        }, _toDisplayString(option.text), 9, _hoisted_2))
      }), 128))
    ]),
    _: 1
  }, 8, ["class", "id", "name", "rules", "label", "modelValue"]))
}
}

})